import { useEffect, useState } from 'react'
import { createSnapshot, fetchSnapshots, type SnapshotRow } from '../../lib/api'
import { useDateRangeCtx } from '../DateRangeContext'
import { useFlowFilter } from '../FlowFilterContext'
import type { Audience, Variant } from '../../types'

const fmt = (n: number) => n.toLocaleString('da-DK')
const fmtPct = (p: number) => `${(p * 100).toFixed(1)}%`

const AUDIENCE_LABEL: Record<Audience, string> = { unge: 'Unge', aeldre: 'Ældre' }
const VARIANT_LABEL: Record<Variant, string> = { chat: 'Chat', calculator: 'Beregner' }

function audienceTag(row: SnapshotRow): Audience | null {
  return row.audience === 'unge' || row.audience === 'aeldre' ? row.audience : null
}
function variantTag(row: SnapshotRow): Variant | null {
  return row.variant === 'chat' || row.variant === 'calculator' ? row.variant : null
}

function formatWhen(ms: number): string {
  const d = new Date(ms)
  return `${d.getDate()}/${d.getMonth() + 1} kl. ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function formatRange(from: number, to: number): string {
  const f = new Date(from)
  const t = new Date(to)
  return `${f.getDate()}/${f.getMonth() + 1} – ${t.getDate()}/${t.getMonth() + 1}`
}

export function Snapshots() {
  const { range } = useDateRangeCtx()
  const { variantParam, audienceParam } = useFlowFilter()
  const [rows, setRows] = useState<SnapshotRow[] | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = () => fetchSnapshots()
    .then((r) => setRows(r.snapshots))
    .catch((e) => setError(String(e)))

  useEffect(() => {
    let cancelled = false
    fetchSnapshots()
      .then((r) => { if (!cancelled) setRows(r.snapshots) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [])

  const save = async () => {
    if (!name.trim() || busy) return
    setBusy(true); setError(null)
    try {
      await createSnapshot(name.trim(), range, variantParam, audienceParam)
      setName('')
      await reload()
    } catch (e) {
      setError(String(e))
    } finally {
      setBusy(false)
    }
  }

  if (error && !rows) return <div className="card card-empty">Snapshots utilgængelige (KV ikke konfigureret)</div>

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Snapshots</div>
        <div className="card-subtitle">Frys nøgletal for nuværende periode — brug til week-over-week sammenligning</div>
      </div>

      <div className="snapshot-form">
        <input
          type="text"
          placeholder={`Fx "Uge ${Math.max(1, Math.ceil((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / (7 * 86400000)))}"`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save() }}
          disabled={busy}
        />
        <button className="btn-action" onClick={save} disabled={busy || name.trim().length === 0}>
          {busy ? 'Gemmer…' : 'Gem snapshot'}
        </button>
      </div>
      <div className="muted small" style={{ marginTop: 6 }}>
        Gemmer KPI for {formatRange(range.from, range.to)}.
      </div>

      {error && <div className="muted small" style={{ marginTop: 8, color: 'var(--danger, #f87171)' }}>{error}</div>}

      {rows === null ? (
        <div className="muted small" style={{ marginTop: 14 }}>Indlæser…</div>
      ) : rows.length === 0 ? (
        <div className="muted small" style={{ marginTop: 14 }}>Ingen snapshots gemt endnu.</div>
      ) : (
        <div className="snapshot-list">
          {rows.map((r) => {
            const aud = audienceTag(r)
            const variant = variantTag(r)
            return (
              <div key={r.savedAt + ':' + r.name} className="snapshot-row">
                <div>
                  <div className="snapshot-name">
                    <span>{r.name}</span>
                    {aud && <span className={`pill audience-${aud}`} style={{ marginLeft: 8 }}>{AUDIENCE_LABEL[aud]}</span>}
                    {variant && <span className={`pill ${variant}`} style={{ marginLeft: 6 }}>{VARIANT_LABEL[variant]}</span>}
                  </div>
                  <div className="snapshot-when">{formatWhen(r.savedAt)} · {formatRange(r.range.from, r.range.to)}</div>
                </div>
                <div className="num">{fmt(r.sessions)}</div>
                <div className="num">{fmt(r.leads)}</div>
                <div className="num">{fmtPct(r.completionRate)}</div>
                <div className="muted small">sessioner / leads / CR</div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
